import { useState, useRef } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Gauge, MagnifyingGlass, PenNib, MapPin, Users, Gear, SquaresFour,
  ChartLineUp, List, X, SignOut, CaretDown, Plus, Binoculars, LinkSimple,
  WarningCircle, CircleNotch, Question, FilePdf,
} from "@phosphor-icons/react";
import { LogoMark, Wordmark } from "@/components/Logo";
import OnboardingTour, { openOnboardingTour } from "@/components/onboarding/OnboardingTour";

const nav = [
  { to: "/portfolio", label: "Portfolio", icon: SquaresFour, testid: "nav-portfolio" },
  { to: "/", label: "Dashboard", icon: Gauge, testid: "nav-dashboard" },
  { to: "/seo-audit", label: "SEO Audit", icon: MagnifyingGlass, testid: "nav-seo-audit" },
  { to: "/rank-tracking", label: "Rank Tracking", icon: ChartLineUp, testid: "nav-rank-tracking" },
  { to: "/competitors", label: "Competitors", icon: Binoculars, testid: "nav-competitors" },
  { to: "/link-building", label: "Link Building", icon: LinkSimple, testid: "nav-link-building" },
  { to: "/content", label: "Content Studio", icon: PenNib, testid: "nav-content" },
  { to: "/local-seo", label: "Local SEO", icon: MapPin, testid: "nav-local-seo" },
  { to: "/conversion", label: "Conversion", icon: Users, testid: "nav-conversion" },
  { to: "/settings", label: "Settings", icon: Gear, testid: "nav-settings" },
];

function WorkspaceSwitcher() {
  const { workspaces, currentWorkspace, selectWorkspace, loadWorkspaces } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  const newProject = async () => {
    const name = prompt("New project name");
    if (!name) return;
    try {
      const r = await api.createWorkspace({ name, domain: "" });
      await loadWorkspaces();
      selectWorkspace(r.data.id);
      setOpen(false);
      toast.success("Project created");
      nav("/settings");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="relative px-3 py-3 border-b" style={{ borderColor: "var(--line)" }}>
      <button data-testid="workspace-switcher" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-3 py-2 border hover:bg-[#0e1626] transition-colors" style={{ borderColor: "var(--line)" }}>
        <div className="min-w-0 text-left">
          <div className="text-[10px] uppercase tracking-wider" style={{ color: "var(--text-2)" }}>Project</div>
          <div className="text-sm font-semibold truncate">{currentWorkspace?.name || "—"}</div>
        </div>
        <CaretDown size={14} />
      </button>
      {open && (
        <div className="absolute left-3 right-3 mt-1 z-30 border shadow-xl" style={{ background: "var(--surface)", borderColor: "var(--line)" }}>
          {workspaces.map((w) => (
            <button key={w.id} data-testid={`workspace-option-${w.id}`} onClick={() => { selectWorkspace(w.id); setOpen(false); }} className="w-full text-left px-3 py-2 text-sm hover:bg-[#0e1626] transition-colors truncate" style={{ color: w.id === currentWorkspace?.id ? "var(--accent)" : "var(--text)" }}>
              {w.name}
            </button>
          ))}
          <button data-testid="new-workspace" onClick={newProject} className="w-full text-left px-3 py-2 text-sm border-t flex items-center gap-2 hover:bg-[#0e1626] transition-colors" style={{ borderColor: "var(--line)", color: "var(--accent)" }}>
            <Plus size={14} /> New project
          </button>
        </div>
      )}
    </div>
  );
}

function VerifyBanner() {
  const { user } = useAuth();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  if (!user || user.email_verified) return null;

  const resend = async () => {
    setSending(true);
    try {
      await api.resendVerification();
      setSent(true);
      toast.success("Verification email sent — check your inbox");
    } catch (e) { toast.error(apiError(e)); } finally { setSending(false); }
  };

  return (
    <div className="px-4 sm:px-8 py-2.5 flex items-center gap-2 text-sm" style={{ background: "rgba(250,204,21,0.08)", borderBottom: "1px solid var(--warning)", color: "var(--warning)" }} data-testid="email-verify-banner">
      <WarningCircle size={16} weight="duotone" />
      <span className="flex-1">Please verify your email ({user.email}) — some AI-powered features (audits, content, competitor analysis, outreach) are disabled until you do.</span>
      <button data-testid="resend-verification" onClick={resend} disabled={sending || sent} className="text-xs font-bold px-3 py-1.5 border hover:bg-black/20 transition-colors disabled:opacity-60 shrink-0" style={{ borderColor: "var(--warning)" }}>
        {sending ? <CircleNotch size={13} className="animate-spin" /> : sent ? "Sent" : "Resend email"}
      </button>
    </div>
  );
}

// Downloadable collateral shown in the user menu. Add future datasheets here.
const DATASHEETS = [
  { label: "JumpSEO Datasheet (PDF)", href: "/JumpSEO-Datasheet.pdf" },
];

function UserMenu() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);

  const openMenu = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => setOpen(false), 180);
  };

  return (
    <div className="relative" onMouseEnter={openMenu} onMouseLeave={scheduleClose}>
      <button
        data-testid="user-menu-button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Help & resources"
        className="w-8 h-8 flex items-center justify-center text-sm font-bold uppercase hover:opacity-90 transition-opacity"
        style={{ background: "var(--primary)", borderRadius: 3 }}
      >
        {(user?.name || "U")[0]}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-30 w-72 border shadow-xl" style={{ background: "var(--surface)", borderColor: "var(--line)" }}>
          <div className="px-4 py-3 border-b" style={{ borderColor: "var(--line)" }}>
            <div className="text-sm font-semibold truncate">{user?.name}</div>
            <div className="mono text-[11px] truncate" style={{ color: "var(--text-2)" }}>{user?.email}</div>
          </div>
          <button
            data-testid="user-menu-replay-tour"
            onClick={() => { openOnboardingTour(); setOpen(false); }}
            className="w-full text-left px-4 py-2.5 text-sm flex items-center gap-2.5 hover:bg-[#0e1626] transition-colors"
          >
            <Question size={16} weight="bold" color="var(--accent)" /> Replay guided tour
          </button>
          <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-wider border-t" style={{ borderColor: "var(--line)", color: "var(--text-2)" }}>
            Datasheets
          </div>
          {DATASHEETS.map((d) => (
            <a
              key={d.href}
              href={d.href}
              target="_blank"
              rel="noreferrer"
              data-testid="user-menu-datasheet"
              onClick={() => setOpen(false)}
              className="w-full text-left px-4 py-2.5 text-sm flex items-center gap-2.5 hover:bg-[#0e1626] transition-colors"
            >
              <FilePdf size={16} weight="bold" color="var(--accent)" /> {d.label}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Layout() {
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();
  const current = nav.find((n) => n.to === loc.pathname)?.label || "";

  const Sidebar = (
    <aside className="w-64 shrink-0 border-r flex flex-col h-full" style={{ background: "var(--surface)", borderColor: "var(--line)" }}>
      <div className="px-5 py-5 border-b flex items-center justify-between" style={{ borderColor: "var(--line)" }}>
        <div className="flex items-center gap-3">
          <LogoMark size={34} />
          <div>
            <Wordmark className="text-[15px] leading-none text-white" />
            <div className="mono text-[10px] tracking-widest mt-1" style={{ color: "var(--accent)" }}>SEO ENGINE</div>
          </div>
        </div>
        <button className="lg:hidden" onClick={() => setMobileOpen(false)}><X size={22} /></button>
      </div>
      <WorkspaceSwitcher />
      <nav className="flex-1 py-3 overflow-y-auto">
        {nav.map((n) => {
          const Icon = n.icon;
          return (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={n.testid} onClick={() => setMobileOpen(false)}
              className={({ isActive }) => `flex items-center gap-3 px-5 py-3 text-sm transition-colors duration-200 border-l-2 ${isActive ? "text-white border-l-[var(--accent)] bg-[#0e1626]" : "text-[var(--text-2)] border-l-transparent hover:text-white hover:bg-[#0e1626]"}`}>
              <Icon size={19} weight="duotone" />{n.label}
            </NavLink>
          );
        })}
      </nav>
      <div className="px-4 py-3 border-t" style={{ borderColor: "var(--line)" }}>
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate">{user?.name}</div>
            <div className="mono text-[11px] truncate" style={{ color: "var(--text-2)" }}>{user?.email}</div>
          </div>
          <button data-testid="logout-button" onClick={logout} title="Log out" className="p-2 hover:bg-[#0e1626] transition-colors" style={{ color: "var(--text-2)" }}><SignOut size={18} /></button>
        </div>
      </div>
    </aside>
  );

  return (
    <div className="flex min-h-screen" style={{ background: "var(--bg)" }}>
      <OnboardingTour />
      <div className="hidden lg:block">{Sidebar}</div>
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0">{Sidebar}</div>
        </div>
      )}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b flex items-center justify-between px-4 sm:px-8" style={{ background: "var(--surface)", borderColor: "var(--line)" }}>
          <div className="flex items-center gap-3">
            <button data-testid="mobile-menu-button" className="lg:hidden" onClick={() => setMobileOpen(true)}><List size={24} /></button>
            <h1 className="text-base sm:text-lg font-bold">{current}</h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline mono text-xs px-3 py-1 border" style={{ borderColor: "var(--line)", color: "var(--success)" }}>● Toronto, CA</span>
            <UserMenu />
          </div>
        </header>
        <VerifyBanner />
        <main className="flex-1 p-4 sm:p-8 overflow-auto"><Outlet /></main>
      </div>
    </div>
  );
}
